import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle } from "../icons";
import type { RosterImportApplyResponse } from "../../lib/rosterImportApi";

export interface ApplyResultSummaryProps {
  result: RosterImportApplyResponse;
}

/** Result step of the roster importer (EPIC-95 AC4): created/linked/skipped
 *  counts plus any per-row errors, shown once after a successful Apply. */
export default function ApplyResultSummary({
  result,
}: ApplyResultSummaryProps) {
  const { t } = useTranslation("admin");

  return (
    <div className="space-y-4" data-testid="roster-import-result">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
          <p
            className="text-2xl font-bold text-green-800"
            data-testid="roster-import-result-created"
          >
            {result.created.length}
          </p>
          <p className="text-xs font-medium text-green-700">
            {t("rosterImport.resultCreated")}
          </p>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
          <p
            className="text-2xl font-bold text-blue-800"
            data-testid="roster-import-result-linked"
          >
            {result.linked.length}
          </p>
          <p className="text-xs font-medium text-blue-700">
            {t("rosterImport.resultLinked")}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
          <p
            className="text-2xl font-bold text-gray-800"
            data-testid="roster-import-result-skipped"
          >
            {result.skipped}
          </p>
          <p className="text-xs font-medium text-gray-700">
            {t("rosterImport.resultSkipped")}
          </p>
        </div>
      </div>

      {result.errors.length > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle
              className="h-5 w-5 text-red-600 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-2">
                {t("rosterImport.resultErrorsTitle")}
              </h3>
              <ul
                className="text-sm text-red-800 space-y-1"
                data-testid="roster-import-result-errors"
              >
                {result.errors.map((error) => (
                  <li key={error.row_index}>
                    {t("rosterImport.resultErrorRow", {
                      row: error.row_index + 1,
                    })}
                    : {error.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <CheckCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t("rosterImport.resultAllOk")}
        </div>
      )}
    </div>
  );
}
